import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, TextInput, FlatList, Platform } from 'react-native';
import { MODULE_COLORS } from '@cubemeet/core/theme';
import { useTravelI18n } from '../useTravelI18n';
import { SUPPORTED_CURRENCIES, POPULAR_CURRENCIES } from '@cubemeet/core/services/currencyService';

interface Props {
  value: string;
  onChange: (v: string) => void;
  compact?: boolean; // render compact button (symbol + code)
  showFlag?: boolean;
}

const CurrencyPicker: React.FC<Props> = ({ value, onChange, compact = false, showFlag = true }) => {
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState('');

  const { t } = useTravelI18n();

  // safe translate with fallback
  const tt = (key: string, fallback: string) => {
    try {
      const v = (t as any)(key);
      if (!v || v === key) return fallback;
      return v;
    } catch (err) {
      return fallback;
    }
  };

  // Map some currency codes to representative country codes for flags (fallback to currency code lowercased)
  const currencyToCountry: Record<string, string> = {
    CNY: 'cn',
    USD: 'us',
    EUR: 'eu',
    GBP: 'gb',
    JPY: 'jp',
    HKD: 'hk',
    TWD: 'tw',
    SGD: 'sg',
    AUD: 'au',
    CAD: 'ca',
    KRW: 'kr',
    THB: 'th',
    MYR: 'my',
    VND: 'vn',
  };

  const getFlagUrlForCurrency = (code: string) => {
    const cc = currencyToCountry[code] || code.toLowerCase();
    return `https://flagcdn.com/${cc}.svg`;
  };

  const selected = useMemo(() => SUPPORTED_CURRENCIES.find((c) => c.code === value), [value]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = SUPPORTED_CURRENCIES;
    if (q) {
      list = list.filter((c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
    }
    // put popular first when no search
    if (!q) {
      const popular = list.filter((c) => POPULAR_CURRENCIES.includes(c.code));
      const others = list.filter((c) => !POPULAR_CURRENCIES.includes(c.code));
      return [...popular, ...others];
    }
    return list;
  }, [search]);

  return (
    <View>
      <TouchableOpacity style={[styles.pickerButton, compact && styles.pickerButtonCompact]} onPress={() => setVisible(true)}>
        <View style={styles.pickerLeft}>
          {showFlag && selected && (
            // prefer CDN SVG, fallback to emoji if image fails
            <>
              <img src={getFlagUrlForCurrency(selected.code)} alt={selected.code} onError={(e: any) => { e.currentTarget.style.display = 'none'; const next = e.currentTarget.nextElementSibling; if (next) next.style.display = 'inline'; }} style={{ width: 28, height: 20, verticalAlign: 'middle', borderRadius: 4 }} />
              <Text style={styles.flag}><span style={{ display: 'none' }}>{selected.flag}</span></Text>
            </>
          )}
          <View style={styles.pickerInfo}>
            <Text style={[styles.pickerCode, compact && styles.pickerCodeCompact]}>{selected ? selected.code : value}</Text>
            {!compact && <Text style={styles.pickerName}>{selected ? selected.name : value}</Text>}
          </View>
        </View>
        <View style={styles.pickerRight}>
          <Text style={[styles.pickerSymbol, compact && styles.pickerSymbolCompact]}>{selected?.symbol || '¤'}</Text>
        </View>
      </TouchableOpacity>

      <Modal visible={visible} transparent animationType="slide" onRequestClose={() => setVisible(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{tt('createTravel.currencyModal.title', '选择货币')}</Text>
              <TouchableOpacity onPress={() => setVisible(false)}>
                <Text style={{ fontSize: 20, color: '#666' }}>✕</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.searchContainer}>
              <TextInput style={styles.searchInput} placeholder={tt('createTravel.currencyModal.searchPlaceholder', '搜索货币名称或代码...')} value={search} onChangeText={setSearch} />
            </View>

            <FlatList
              data={filtered}
              keyExtractor={(i) => i.code}
              renderItem={({ item }) => (
                <TouchableOpacity style={[styles.currencyItem, item.code === value && styles.selectedItem]} onPress={() => { onChange(item.code); setVisible(false); setSearch(''); }}>
                  <View style={styles.currencyLeft}>
                    {showFlag && (
                      <>
                        <img src={getFlagUrlForCurrency(item.code)} alt={item.code} onError={(e: any) => { e.currentTarget.style.display = 'none'; const next = e.currentTarget.nextElementSibling; if (next) next.style.display = 'inline'; }} style={{ width: 28, height: 20, verticalAlign: 'middle', borderRadius: 4 }} />
                        <Text style={styles.flag}><span style={{ display: 'none' }}>{item.flag}</span></Text>
                      </>
                    )}
                    <View style={styles.currencyInfo}>
                      <View style={styles.currencyHeader}>
                        <Text style={[styles.currencyCode, item.code === value && styles.selectedText]}>{item.code}</Text>
                        {POPULAR_CURRENCIES.includes(item.code) && <View style={styles.popularBadge}><Text style={styles.popularText}>{tt('createTravel.currencyModal.popularBadge', '热')}</Text></View>}
                      </View>
                      <Text style={[styles.currencyName, item.code === value && styles.selectedText]}>{item.name}</Text>
                    </View>
                  </View>
                  <View style={styles.currencyRight}>
                    <Text style={[styles.currencySymbol, item.code === value && styles.selectedText]}>{item.symbol}</Text>
                  </View>
                </TouchableOpacity>
              )}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
            />
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  pickerButtonCompact: {
    width: 96,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 8,
  },
  pickerLeft: { flexDirection: 'row', alignItems: 'center' },
  pickerInfo: { marginLeft: 8 },
  pickerCode: { fontSize: 15, fontWeight: '600', color: '#111827' },
  pickerCodeCompact: { fontSize: 14 },
  pickerName: { fontSize: 13, color: '#6B7280', marginTop: 2 },
  pickerRight: { flexDirection: 'row', alignItems: 'center' },
  pickerSymbol: { fontSize: 16, fontWeight: '600', color: MODULE_COLORS.travel, marginLeft: 8 },
  pickerSymbolCompact: { fontSize: 14 },

  flag: { fontSize: 18, fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, "Segoe UI Symbol", "Noto Color Emoji", sans-serif' },
  modalOverlay: { position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modalContent: { width: '90%', maxWidth: 480, alignSelf: 'center', maxHeight: '80%', backgroundColor: '#fff', borderRadius: 12, padding: 12 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, paddingHorizontal: 4 },
  modalTitle: { fontSize: 16, fontWeight: '700' },
  searchContainer: { marginVertical: 8, paddingHorizontal: 6 },
  searchInput: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, padding: 8 },
  currencyItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, paddingHorizontal: 6 },
  selectedItem: { backgroundColor: '#FFF7ED' },
  currencyLeft: { flexDirection: 'row', alignItems: 'center' },
  currencyInfo: { marginLeft: 8 },
  currencyHeader: { flexDirection: 'row', alignItems: 'center' },
  currencyCode: { fontSize: 14, fontWeight: '600' },
  popularBadge: { backgroundColor: '#FF9800', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2, marginLeft: 8 },
  popularText: { color: '#fff', fontSize: 11, fontWeight: '700' },
  currencyName: { fontSize: 13, color: '#6B7280' },
  currencyRight: { alignItems: 'center' },
  currencySymbol: { fontSize: 16, fontWeight: '600', color: MODULE_COLORS.travel },
  separator: { height: 1, backgroundColor: '#F0F0F0' },
  selectedText: { color: MODULE_COLORS.travel },
});

export default CurrencyPicker;
